// src/api/client.ts

import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import { API_BASE_URL, TOKEN_KEY, USER_KEY } from '../constants';
import { cacheResponseInterceptor, offlineFallbackInterceptor } from './offlineCacheInterceptor';

// Create axios instance
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  // Required for the HttpOnly auth cookie to be sent cross-origin
  withCredentials: true,
});

// Request interceptor — attach Authorization header + Accept-Language + x-table-token
apiClient.interceptors.request.use(
  (config) => {
    // Primary auth: token in Authorization header (reliable cross-origin)
    const token = localStorage.getItem(TOKEN_KEY);
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // Table session token (for dine-in orders via QR scan)
    const tableToken = sessionStorage.getItem('tableSessionToken');
    if (tableToken) {
      config.headers['x-table-token'] = tableToken;
    }
    const lang = localStorage.getItem('appLanguage') || 'ar';
    config.headers['Accept-Language'] = lang;
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor #1 — Cache successful GET responses to IndexedDB
apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    // Fire-and-forget: cache the response in the background
    cacheResponseInterceptor(response).catch(() => {});
    return response;
  },
  undefined // errors handled by the next interceptor
);

// Response interceptor #2 — Offline fallback (cache reads + mutation queuing)
apiClient.interceptors.response.use(
  undefined, // success pass-through
  async (error: AxiosError) => {
    // First: try offline fallback for network errors
    const isNetworkError = !error.response && (error.code === 'ERR_NETWORK' || error.message?.includes('Network Error'));
    if (isNetworkError) {
      try {
        return await offlineFallbackInterceptor(error);
      } catch {
        // Offline fallback didn't handle it — fall through to regular error handling
      }
    }

    // Regular error handling (401 auto-logout etc.)
    const url = error.config?.url ?? '';
    // Skip auto-logout for auth routes — let those callers handle 401 themselves:
    // • /auth/profile — startup session-check, AuthContext handles 401 by clearing state
    // • /auth/login   — wrong credentials, login form shows the error
    // • /auth/logout  — would create an infinite loop
    const isAuthRoute =
      url.includes('/auth/profile') ||
      url.includes('/auth/login') ||
      url.includes('/auth/logout');

    if (error.response?.status === 401 && !isAuthRoute) {
      const wasAuthenticated = !!localStorage.getItem(USER_KEY);
      if (wasAuthenticated) {
        apiClient.post('/auth/logout').catch(() => { });
        localStorage.removeItem(USER_KEY);
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Helper functions
export const get = <T = any>(url: string, config?: AxiosRequestConfig) => {
  return apiClient.get<T>(url, config);
};

export const post = <T = any>(url: string, data?: any, config?: AxiosRequestConfig) => {
  return apiClient.post<T>(url, data, config);
};

export const put = <T = any>(url: string, data?: any, config?: AxiosRequestConfig) => {
  return apiClient.put<T>(url, data, config);
};

export const patch = <T = any>(url: string, data?: any, config?: AxiosRequestConfig) => {
  return apiClient.patch<T>(url, data, config);
};

export const del = <T = any>(url: string, config?: AxiosRequestConfig) => {
  return apiClient.delete<T>(url, config);
};

// Upload helper with FormData
export const upload = <T = any>(url: string, formData: FormData, config?: AxiosRequestConfig) => {
  return apiClient.post<T>(url, formData, {
    ...config,
    headers: {
      'Content-Type': 'multipart/form-data',
      ...config?.headers,
    },
  });
};

export const uploadPut = <T = any>(url: string, formData: FormData, config?: AxiosRequestConfig) => {
  return apiClient.put<T>(url, formData, {
    ...config,
    headers: {
      'Content-Type': 'multipart/form-data',
      ...config?.headers,
    },
  });
};

export default apiClient;